import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { apiFetch } from '../lib/api';

function parseAnyDateTime(v) {
  if (!v) return null;
  if (v instanceof Date) {
    const dt = DateTime.fromJSDate(v, { zone: 'utc' });
    return dt.isValid ? dt : null;
  }
  const s = String(v);
  let dt = DateTime.fromISO(s, { zone: 'utc' });
  if (!dt.isValid) dt = DateTime.fromSQL(s, { zone: 'utc' });
  if (!dt.isValid) dt = DateTime.fromRFC2822(s, { zone: 'utc' });
  return dt.isValid ? dt : null;
}

function fmtGroupAndLocal(isoUtc, groupZone, localZone) {
  const dt = parseAnyDateTime(isoUtc);
  if (!dt) return { group: '—', local: '—' };

  const gz = groupZone || 'utc';
  const lz = localZone || DateTime.local().zoneName || 'utc';

  const group = dt.setZone(gz).toFormat('dd LLL yyyy, h:mm:ss a');
  const local = dt.setZone(lz).toFormat('dd LLL yyyy, h:mm:ss a');
  return { group, local };
}

function normalizeFromFlatRows(rows, nameById = {}) {
  const out = [];
  for (const b of rows || []) {
    // Support many shapes (server returns different keys across routes)
    const period_number = Number(b.period_number ?? b.periodNumber ?? b.period ?? 0) || 0;
    const membership_index = Number(b.membership_index ?? b.membershipIndex ?? b.membership ?? 1) || 1;
    const user_id = Number(b.user_id ?? b.userId ?? b.user ?? 0) || 0;

    const amount = (() => {
      if (typeof b.amount === 'number') return b.amount;
      if (typeof b.bid === 'number') return b.bid;
      if (typeof b.bid_amount === 'number') return b.bid_amount;
      const n = Number(b.amount ?? b.bid ?? b.bid_amount);
      return Number.isFinite(n) ? n : 0;
    })();

    const bid_time = b.bid_time ?? b.time ?? b.bidTime ?? b.timestamp ?? b.created_at ?? null;

    const is_winner = !!(b.is_winner ?? b.isWinner);
    // Do not expose "auto bid" vs "manual" to other users — treat it as a normal bid.
    const rawMethod = String(b.method ?? (b.is_lucky_dip ? 'lucky_dip' : 'bid') ?? 'bid').toLowerCase();
    const method = (rawMethod === 'auto_bid' || rawMethod === 'manual' || rawMethod === 'socket') ? 'bid' : rawMethod;

    const bidderName =
      b.bidderName ||
      b.bidder_name ||
      b.winner_label ||
      b.winnerName ||
      nameById[user_id] ||
      (user_id ? `User #${user_id}` : '—');

    out.push({
      id: b.id ?? `${period_number}-${user_id}-${membership_index}-${bid_time ?? ''}-${amount}`,
      period_number,
      membership_index,
      user_id,
      amount,
      bid_time,
      is_winner,
      method,
      bidderName,
    });
  }

  out.sort((a, b) => {
    if (a.period_number !== b.period_number) return b.period_number - a.period_number;
    const ta = parseAnyDateTime(a.bid_time)?.toMillis?.() ?? 0;
    const tb = parseAnyDateTime(b.bid_time)?.toMillis?.() ?? 0;
    // newest first
    return tb - ta;
  });

  return out;
}

function groupRowsByPeriod(rows) {
  const by = new Map();
  for (const r of rows || []) {
    const p = Number(r.period_number || 0) || 0;
    if (!by.has(p)) by.set(p, []);
    by.get(p).push(r);
  }
  const periods = Array.from(by.keys()).sort((a, b) => b - a);
  return periods.map((p) => ({ period: p, rows: by.get(p) }));
}

function prettyMethod(method) {
  const m = String(method || 'bid').toLowerCase();
  if (m === 'bid') return 'Bid';
  if (m === 'lucky_dip') return 'Lucky Dip';
  if (m === 'organiser-benefit') return 'Organiser benefit';
  return m.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

async function readJsonResponse(res, url) {
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (!ct.includes('application/json')) {
    const text = await res.text().catch(() => '');
    const sample = text ? text.slice(0, 200).replace(/\s+/g, ' ').trim() : '';
    const err = new Error(
      `Expected JSON from ${url} but got ${ct || 'unknown content-type'}${sample ? `: ${sample}` : ''}`
    );
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export default function BidHistoryModal({ open, onClose, groupId }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [groupMeta, setGroupMeta] = useState(null);
  const [localZone] = useState(() => DateTime.local().zoneName || 'utc');

  const overlayRef = useRef(null);
  const closeBtnRef = useRef(null);

  const isOpen = !!open;
  const gid = Number(groupId) || 0;

  const groupZone = groupMeta?.timezone || groupMeta?.time_zone || groupMeta?.group_timezone || 'utc';

  const doClose = () => {
    try {
      onClose?.();
    } catch (_) {
      // never block
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === 'Escape') doClose();
    };
    window.addEventListener('keydown', onKey);

    // Focus close button for accessibility and to ensure keyboard works.
    setTimeout(() => {
      try { closeBtnRef.current?.focus?.(); } catch {}
    }, 0);

    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!gid) {
      setErr('Missing group id');
      setPeriods([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setErr(null);

        // Grab group meta for timezone label
        try {
          const r = await apiFetch(`/api/groups/${gid}`);
          if (r.ok) {
            const meta = await readJsonResponse(r, `/api/groups/${gid}`);
            if (!cancelled) setGroupMeta(meta);
          }
        } catch (_) {
          // ok to ignore timezone
        }

        const endpoints = [
          `/api/bidding/previous/${gid}?limit=200`,
          `/api/bidding/previous/${gid}`,
        ];

        let lastErr = null;
        let found = [];

        for (const ep of endpoints) {
          try {
            const res = await apiFetch(ep);
            if (!res.ok) {
              // read JSON error if present, else generic
              try {
                const j = await readJsonResponse(res, ep);
                lastErr = new Error(j?.error || `Failed to load bid history (${res.status})`);
              } catch {
                lastErr = new Error(`Failed to load bid history (${res.status})`);
              }
              continue;
            }

            const json = await readJsonResponse(res, ep);
            const arr = Array.isArray(json)
              ? json
              : (Array.isArray(json?.data) ? json.data : (Array.isArray(json?.rows) ? json.rows : []));

            const rows = normalizeFromFlatRows(arr);
            if (rows.length) {
              found = rows;
              break;
            }
          } catch (e) {
            lastErr = e;
          }
        }

        // If we didn't get rows, still try to infer whether bidding exists (helps debugging).
        if (!found.length) {
          try {
            const res = await apiFetch(`/api/bidding/current/${gid}`);
            if (res.ok) {
              const cur = await readJsonResponse(res, `/api/bidding/current/${gid}`);
              if (cur?.best_bid_amount != null) {
                // Build a single-row "best bid" view so the user sees something.
                found = normalizeFromFlatRows([
                  {
                    id: `current-${gid}-${cur.period}`,
                    userId: null,
                    amount: Number(cur.best_bid_amount || 0),
                    time: cur.best_bid_time || null,
                    membershipIndex: cur.best_membership_index ?? 1,
                    periodNumber: cur.period ?? 0,
                    method: 'current_bid',
                    bidderName: cur.best_bidder || '—',
                  },
                ]);
                lastErr = lastErr || new Error(
                  'Bid history table returned no rows, but a current best bid exists. This usually means bid_logs is not being written/queried correctly.'
                );
              }
            }
          } catch (_) {
            // ignore
          }
        }

        if (cancelled) return;

        if (!found.length && lastErr) {
          setErr(lastErr.message || String(lastErr));
          setPeriods([]);
          return;
        }

        const grouped = groupRowsByPeriod(found);
        setPeriods(grouped);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, gid]);

  const body = useMemo(() => {
    if (!isOpen) return null;

    if (loading) {
      return <div style={{ padding: 16 }}>Loading bid history…</div>;
    }

    if (err) {
      return (
        <div style={{ padding: 16, color: '#b00020' }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Couldn’t load bid history</div>
          <div style={{ marginBottom: 10 }}>{err}</div>
          <div style={{ fontSize: 12, opacity: 0.8 }}>
            Group ID: {gid || '—'}
          </div>
        </div>
      );
    }

    if (!periods.length) {
      return (
        <div style={{ padding: 16 }}>
          <div style={{ opacity: 0.8 }}>No bids recorded yet.</div>
          <div style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>
            Group ID: {gid || '—'}
          </div>
        </div>
      );
    }

    return (
      <div style={{ padding: 16, maxHeight: 420, overflow: 'auto' }}>
        {periods.map((p) => (
          <div key={p.period} style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Period {p.period}</div>

            <div style={{ width: '100%', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>Time</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>Bidder</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>Membership</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>Amount</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>Method</th>
                    <th style={{ padding: '6px 8px', borderBottom: '1px solid #eee' }}>Winner</th>
                  </tr>
                </thead>
                <tbody>
                  {p.rows.map((r) => {
                    const { group, local } = fmtGroupAndLocal(r.bid_time, groupZone, localZone);
                    return (
                      <tr key={r.id}>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f3f3' }}>
                          <div style={{ fontSize: 12, opacity: 0.85 }}>{group}</div>
                          <div style={{ fontSize: 11, opacity: 0.6 }}>{localZone !== groupZone ? `Your time: ${local}` : ''}</div>
                        </td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f3f3' }}>{r.bidderName}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f3f3' }}>#{r.membership_index}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f3f3' }}>{Number(r.amount || 0).toFixed(2)}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f3f3' }}>{prettyMethod(r.method)}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f3f3f3' }}>{r.is_winner ? '✅' : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    );
  }, [err, gid, groupZone, isOpen, loading, localZone, periods]);

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label="Bid history"
      onMouseDown={(e) => {
        // Click on backdrop closes
        if (e.target === overlayRef.current) doClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        pointerEvents: 'auto',
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: 'min(760px, 96vw)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
          overflow: 'hidden',
          pointerEvents: 'auto',
        }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>Bid History</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              Group timezone: {groupZone || '—'} • Your time: {localZone || '—'}
            </div>
          </div>

          <button
            ref={closeBtnRef}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              doClose();
            }}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: '1px solid #ddd',
              background: '#fff',
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: '28px',
            }}
            aria-label="Close"
            title="Close"
          >
            ×
          </button>
        </div>

        {body}

        <div style={{ padding: 12, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={doClose}
            style={{
              padding: '8px 12px',
              borderRadius: 10,
              border: '1px solid #ddd',
              background: '#fff',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
